import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Coffee, Gift, X } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import OptimizedImage from './OptimizedImage';
import LottieStampCup, { STAMP_STAGGER_MS, StaticCup } from './LottieStampCup';
import AppRequiredSheet from './AppRequiredSheet';
import { getClientPlatform } from '../utils/nativePayment';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ——— Pre-order / pre-login teaser (FreeCoffeeTeaser) timing — mirrors the signed-off mock ———
const TEASER_DEFAULT_COUNT = 5;            // cups shown before login (program assumed on)
const T_STAGGER = 110, T_SWEEP = 950, T_IN = 380, T_HOLD = 350, T_VANISH = 260;
const T_SETTLE_FADE = 320, T_BOUNCE_DUR = 560, T_BOUNCE_LEAD = 200;
const T_OVERSHOOT = 'cubic-bezier(0.34,1.56,0.64,1)';
const T_EASE_OUT = 'cubic-bezier(0.22,1,0.36,1)';
const T_EASE_IN = 'cubic-bezier(0.4,0,1,1)';
const KF_CUP_IN = [{ opacity: 0, transform: 'translateX(-50%) translateY(7px) scale(.35)' }, { opacity: 1, transform: 'translateX(-50%) translateY(0) scale(1)' }];
const KF_CUP_OUT = [{ opacity: 1, transform: 'translateX(-50%) translateY(0) scale(1)' }, { opacity: 0, transform: 'translateX(-50%) translateY(8px) scale(.5)' }];
const KF_CUP_BOUNCE = [
  { offset: 0, opacity: 0, transform: 'translateX(-50%) translateY(4px) scale(.2)' },
  { offset: 0.5, opacity: 1, transform: 'translateX(-50%) translateY(-7px) scale(1.22)' },
  { offset: 0.75, opacity: 1, transform: 'translateX(-50%) translateY(0) scale(.93)' },
  { offset: 1, opacity: 1, transform: 'translateX(-50%) translateY(0) scale(1)' },
];
const KF_CUP_BREATHE = [{ opacity: 1, transform: 'translateX(-50%) scale(1)' }, { opacity: .3, transform: 'translateX(-50%) scale(.94)' }];

/**
 * Loyalty tracker with 3 visual states: promo, progress, and reward-ready.
 * The full (non-compact) progress row renders each earned coffee as the branded
 * Penguin Bean takeaway-cup Lottie (<StampSlot>/<LottieStampCup>): on app open the
 * earned cups bounce + zoom in staggered left→right while the orange progress bar
 * fills left→right in sync; a cup earned since the customer last looked (delta via
 * a per-user localStorage seen-count) additionally gets an ink-ripple + haptic. All
 * motion is disabled under prefers-reduced-motion (static cups + instant bar fill).
 * The compact variant stays calm (progress dots, no cups/animation).
 * @param {boolean} compact - if true renders a smaller inline version for order cards (no animation)
 * @param {boolean} hideClaimCta - full card only: suppress the "Claim Now" button (used on the Offers page, where the reward picker below is the claim action — the button would otherwise self-navigate to /offer-of-the-day)
 */
export function FreeCoffeeProgress({ compact = false, hideClaimCta = false }) {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const reducedMotion = useReducedMotion();
  const [progress, setProgress] = useState(null);
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [claiming, setClaiming] = useState(false);
  // App-download claim gate (Business Rule #5 amendment): true opens the
  // "get the app to claim" sheet instead of the claim flow. Only ever set on
  // web — the backend flag is false once an account has been seen on the app.
  const [showAppSheet, setShowAppSheet] = useState(false);
  // Previous seen count when the card last loaded; cups in [earnedFrom, current)
  // "stamp in" this visit. null = no genuine increase to celebrate.
  const [earnedFrom, setEarnedFrom] = useState(null);
  // App-open intro: once the full card mounts with data, the earned cups bounce
  // in (staggered left→right) and the orange bar fills in sync. Fires once per
  // mount (every app open / Landing visit); never under reduced motion.
  const [intro, setIntro] = useState(false);
  const introFiredRef = useRef(false);

  useEffect(() => {
    if (user && token) fetchProgress();
  }, [user, token]);

  /**
   * Lock background scroll while the claim modal is open so the page behind
   * the hand-rolled fixed overlay can't scroll. Restores the prior inline
   * overflow value on close/unmount.
   */
  useEffect(() => {
    if (!showClaimModal) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, [showClaimModal]);

  // Kick off the app-open intro a beat after the full card has data, so the
  // staggered cup bounce + bar fill play once the tracker is on screen. Skipped
  // in the compact variant and under reduced motion (cups render static).
  useEffect(() => {
    if (compact || reducedMotion || introFiredRef.current) return;
    if (!progress?.enabled) return;
    introFiredRef.current = true;
    const t = setTimeout(() => setIntro(true), 60);
    return () => clearTimeout(t);
  }, [compact, reducedMotion, progress]);

  const fetchProgress = async () => {
    try {
      const res = await axios.get(`${API}/free-coffee/progress`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Only the full Landing card celebrates an earned stamp — the compact
      // (OrderTracking) variant stays calm and must NOT consume the delta, or
      // it would steal the celebration before the customer reaches Home.
      if (!compact) detectEarned(res.data);
      setProgress(res.data);
    } catch {}
  };

  /**
   * Decide which stamps (if any) the customer earned since they last looked, by
   * comparing the freshly-fetched count against a per-user last-seen value in
   * localStorage. Sets `earnedFrom` to the previous count when it grew (so cups
   * in [earnedFrom, current) stamp in) or null otherwise, then writes the new
   * count through. Writing on every fetch means a post-redemption reset
   * self-corrects and the celebration only ever fires on a genuine increase.
   * First load (no stored value) sets the baseline silently — no retroactive
   * stamping of progress the customer never watched accrue. The count is
   * per-user, not branch-scoped (backend computes it across all the user's
   * completed orders), so the key is keyed on user id alone.
   */
  const detectEarned = (data) => {
    if (!data?.enabled || !user?.id) { setEarnedFrom(null); return; }
    const key = `pbc_fc_seen_${user.id}`;
    const cur = Number(data.current_count) || 0;
    let prev = null;
    try {
      const raw = localStorage.getItem(key);
      if (raw != null) { const n = parseInt(raw, 10); if (Number.isFinite(n)) prev = n; }
    } catch {}
    setEarnedFrom(prev != null && cur > prev ? prev : null);
    try { localStorage.setItem(key, String(cur)); } catch {}
  };

  const handleClaim = async (itemId) => {
    if (progress?.claim_requires_app) { setShowClaimModal(false); setShowAppSheet(true); return; }
    setClaiming(true);
    try {
      // `platform` is the server's mark-app-seen backstop (Business Rule #5
      // app-claim gate) — a fresh native install whose launch ping failed can
      // still claim in-app.
      const res = await axios.post(`${API}/free-coffee/claim`, { item_id: itemId, platform: getClientPlatform() }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      setShowClaimModal(false);
      fetchProgress();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to claim');
    } finally { setClaiming(false); }
  };

  // FULL card — the teaser covers every "not yet earned" state: logged-out, a fresh
  // logged-in customer (0 earned), AND the window while the authed `/free-coffee/
  // progress` fetch is still in flight or a silent failure (catch {}) left `progress`
  // null. This keeps the tracker on screen the moment Home paints (matching pre-login)
  // instead of a blank gap — the bug where a logged-in 0-order customer saw nothing
  // until/unless the fetch resolved. Only a confirmed-started customer (count > 0 or
  // claimable) or a confirmed-disabled program peels away from the teaser.
  if (!compact) {
    const confirmedDisabled = progress && progress.enabled === false;
    const started = !!progress?.enabled && ((progress.current_count || 0) > 0 || progress.can_claim);
    if (!confirmedDisabled && !started) {
      const teaserCount = (progress?.enabled && progress.required_count) || TEASER_DEFAULT_COUNT;
      return <FreeCoffeeTeaser requiredCount={teaserCount} reducedMotion={!!reducedMotion} />;
    }
  }

  if (!progress || !progress.enabled) return null;

  const { current_count, required_count, can_claim, has_active_claim, remaining, reward_items } = progress;
  const percent = Math.min(100, (current_count / required_count) * 100);
  const hasStarted = current_count > 0;
  const rewardReady = can_claim || has_active_claim;

  // App-download claim gate (Business Rule #5 amendment). Tracker + counts
  // render exactly as before; only the CLAIM tap reroutes to the download
  // sheet. False inside the native app and for accounts already seen on it.
  const claimGated = !!progress.claim_requires_app;
  const handleClaimNow = () => {
    if (claimGated) { setShowAppSheet(true); return; }
    navigate('/offer-of-the-day');
  };

  // Compact version for order tracking
  if (compact) {
    if (rewardReady) {
      return (
        <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl" data-testid="free-coffee-compact-ready">
          <div className="flex items-center gap-2 mb-2">
            <Gift className="w-5 h-5 text-green-600" />
            <span className="font-semibold text-green-800">Congrats! You've unlocked a free coffee!</span>
          </div>
          <Button size="sm" className="rounded-full bg-green-600 hover:bg-green-700" onClick={handleClaimNow} data-testid="claim-now-btn">
            Claim Now
          </Button>
          <AppRequiredSheet open={showAppSheet} onOpenChange={setShowAppSheet} />
        </div>
      );
    }
    if (!hasStarted) {
      return (
        <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl" data-testid="free-coffee-compact-promo">
          <div className="flex items-center gap-2">
            <Coffee className="w-4 h-4 text-amber-700 flex-shrink-0" />
            <span className="text-sm font-medium text-amber-900">Place {required_count} coffee orders to unlock a free coffee</span>
          </div>
        </div>
      );
    }
    return (
      <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl" data-testid="free-coffee-compact-progress">
        <div className="flex items-center gap-2 mb-2">
          <Coffee className="w-4 h-4 text-amber-700" />
          <span className="text-sm font-medium text-amber-900">
            {remaining} more coffee order{remaining !== 1 ? 's' : ''} to unlock your free coffee
          </span>
        </div>
        <div className="flex gap-1">
          {Array.from({ length: required_count }).map((_, i) => (
            <div key={i} className={`h-2 flex-1 rounded-full ${i < current_count ? 'bg-amber-500' : 'bg-amber-200'}`} />
          ))}
        </div>
      </div>
    );
  }

  // Full version — progress tracker (count >= 1 or can_claim); the not-started full
  // card already returned the FreeCoffeeTeaser above, so only started/claimable
  // customers reach here.
  return (
    <Card className="p-5 border-2 border-orange-400 bg-white overflow-hidden shadow-md shadow-orange-500/20" data-testid="free-coffee-progress-card">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <div className="w-9 h-9 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
            <Coffee className="w-5 h-5 text-orange-600" />
          </div>
          <div className="min-w-0">
            <h3 className="font-bold text-sm truncate">Free Coffee Tracker</h3>
            <p className="text-xs text-muted-foreground">{current_count}/{required_count} orders</p>
          </div>
        </div>
        {rewardReady && (
          <Badge className="bg-green-600 text-white animate-pulse flex-shrink-0" data-testid="free-badge">FREE!</Badge>
        )}
      </div>

      {/* Progress stamps — each earned coffee is a tiny steaming cup; the
          reward-tipping cup gets a gold flourish, and any cup earned since the
          customer last looked stamps in on this visit. Gap-based to stay
          readable on narrow viewports; the trailing gift slot keeps its
          existing ready-state treatment. */}
      <div className="flex justify-between items-end gap-1 mb-3">
        {Array.from({ length: required_count }).map((_, i) => {
          const filled = i < current_count;
          return (
            <StampSlot
              key={i}
              index={i}
              filled={filled}
              isTerminal={i === required_count - 1}
              justEarned={!reducedMotion && earnedFrom != null && filled && i >= earnedFrom}
              intro={intro}
              reducedMotion={!!reducedMotion}
            />
          );
        })}
        <div className="flex flex-col items-center justify-end flex-shrink-0 h-9 sm:h-10">
          <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center transition-all ${
            can_claim ? 'bg-green-500 text-white scale-110 animate-bounce' : 'bg-green-100 text-green-400'
          }`}>
            <Gift className="w-4 h-4" />
          </div>
        </div>
      </div>

      {/* Progress bar — fills left→right on app open, in sync with the cup row */}
      <div className="w-full h-2 bg-amber-100 rounded-full overflow-hidden mb-3">
        <motion.div
          className="h-full bg-gradient-to-r from-amber-400 to-amber-600 rounded-full"
          initial={reducedMotion ? false : { width: '0%' }}
          animate={{ width: `${percent}%` }}
          transition={reducedMotion ? { duration: 0 } : { duration: 0.9, ease: 'easeOut', delay: 0.08 }}
        />
      </div>

      {rewardReady ? (
        <>
          <p className="text-center text-sm font-semibold text-green-700 mb-2 max-w-[260px] mx-auto">
            Congrats, you have unlocked a free coffee!
          </p>
          {!hideClaimCta && (
            <Button className="w-full rounded-full bg-green-600 hover:bg-green-700 text-white" onClick={handleClaimNow} data-testid="claim-now-btn">
              <Gift className="w-4 h-4 mr-2" /> Claim Now
            </Button>
          )}
        </>
      ) : (
        <p className="text-center text-sm text-orange-600 max-w-[260px] mx-auto leading-snug">
          <strong className="text-orange-700">{remaining}</strong> more coffee order{remaining !== 1 ? 's' : ''} to unlock your free coffee
        </p>
      )}

      {showClaimModal && <ClaimModal items={reward_items} onClaim={handleClaim} onClose={() => setShowClaimModal(false)} claiming={claiming} />}
      <AppRequiredSheet open={showAppSheet} onOpenChange={setShowAppSheet} />
    </Card>
  );
}

/**
 * FreeCoffeeTeaser — the pre-order / pre-login promo state of the loyalty tracker.
 * Renders the SAME structure as the earned card (header + N-cup row + bar + gift
 * slot) at 0 earned, then plays a one-shot "once → settle" tease on mount: cup #1
 * bounces in, cups 2→N cascade after it while the bar fills left→right; then the
 * bar recedes right→left as cups drain N→2, and cup #1 stays and gently breathes
 * (slots 2→N resolve to numbered placeholders). Driven by the Web Animations API
 * on refs (mirrors the signed-off mock); under prefers-reduced-motion it renders
 * the settled state statically (no timeline). OTA-safe — no Lottie, no native.
 * Used by: `FreeCoffeeProgress` (logged-out full card + logged-in 0-earned promo)
 * @param {number} requiredCount - cups to show (real count when logged in, else 5)
 * @param {boolean} reducedMotion - render the settled state without animation
 */
function FreeCoffeeTeaser({ requiredCount = TEASER_DEFAULT_COUNT, reducedMotion = false }) {
  const N = Math.max(1, requiredCount || TEASER_DEFAULT_COUNT);
  const cupRefs = useRef([]);
  const phRefs = useRef([]);
  const fillRef = useRef(null);

  useEffect(() => {
    if (reducedMotion) return;
    const cups = cupRefs.current.slice(0, N);
    const phs = phRefs.current.slice(0, N);
    const fill = fillRef.current;
    if (!fill || cups.some((c) => !c)) return;

    const anims = [];
    const timers = [];
    let breathing = null;
    const A = (el, kf, opts) => { const a = el.animate(kf, { fill: 'both', ...opts }); anims.push(a); return a; };

    // FORWARD — cup #1 bounces in, cups 2→N cascade after it, bar fills left→right
    A(cups[0], KF_CUP_BOUNCE, { delay: 0, duration: T_BOUNCE_DUR, easing: 'ease-out' });
    for (let i = 1; i < N; i++)
      A(cups[i], KF_CUP_IN, { delay: T_BOUNCE_LEAD + (i - 1) * T_STAGGER, duration: T_IN, easing: T_OVERSHOOT });
    const forwardEnd = N >= 2 ? T_BOUNCE_LEAD + (N - 2) * T_STAGGER + T_IN : T_BOUNCE_DUR;
    A(fill, [{ width: '0%' }, { width: '100%' }], { delay: 0, duration: forwardEnd, easing: T_EASE_OUT });

    // BACKWARD — bar recedes right→left while cups drain N→2 (cup #1 is spared)
    const tearStart = forwardEnd + T_HOLD;
    A(fill, [{ width: '100%' }, { width: '0%' }], { delay: tearStart, duration: T_SWEEP, easing: 'ease-in-out' });
    for (let i = N - 1; i >= 1; i--)
      A(cups[i], KF_CUP_OUT, { delay: tearStart + (N - 1 - i) * (T_SWEEP / N), duration: T_VANISH, easing: T_EASE_IN });
    for (let i = 1; i < N; i++)
      A(phs[i], [{ opacity: 0 }, { opacity: 1 }], { delay: tearStart + (N - 1 - i) * (T_SWEEP / N) + T_VANISH * 0.55, duration: T_SETTLE_FADE });

    // SETTLE — cup #1 keeps gently fading in/out as the "start here" nudge
    const settleAt = tearStart + (N - 1) * (T_SWEEP / N);
    timers.push(setTimeout(() => {
      breathing = cups[0].animate(KF_CUP_BREATHE, { duration: 1800, iterations: Infinity, direction: 'alternate', easing: 'ease-in-out', fill: 'both' });
    }, settleAt));

    return () => {
      anims.forEach((a) => { try { a.cancel(); } catch {} });
      timers.forEach(clearTimeout);
      if (breathing) { try { breathing.cancel(); } catch {} }
    };
  }, [reducedMotion, N]);

  return (
    <Card className="p-5 border-2 border-orange-400 bg-white overflow-hidden shadow-md shadow-orange-500/20" data-testid="free-coffee-promo-card">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
          <Coffee className="w-5 h-5 text-orange-600" />
        </div>
        <div className="min-w-0">
          <h3 className="font-bold text-sm truncate">Free Coffee Tracker</h3>
          <p className="text-xs text-muted-foreground">0/{N} orders</p>
        </div>
      </div>

      {/* Cup row — identical structure to the earned tracker; cups animate on top of
          numbered placeholders so the settle state matches the real empty slots. */}
      <div className="flex justify-between items-end gap-1 mb-3">
        {Array.from({ length: N }).map((_, i) => (
          <div key={i} className="relative flex-shrink-0 h-9 sm:h-10 w-7 sm:w-8">
            <div
              ref={(el) => (phRefs.current[i] = el)}
              className="absolute bottom-0 left-1/2 -translate-x-1/2 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white border-[1.5px] border-gray-300 text-gray-400 flex items-center justify-center text-xs font-bold"
              style={{ opacity: reducedMotion ? (i === 0 ? 0 : 1) : 0 }}
            >
              {i + 1}
            </div>
            <div
              ref={(el) => (cupRefs.current[i] = el)}
              className="absolute bottom-0 w-7 h-9 sm:w-8 sm:h-10"
              style={{ left: '50%', transform: 'translateX(-50%)', opacity: reducedMotion ? (i === 0 ? 1 : 0) : 0 }}
            >
              <StaticCup className="w-full h-full" />
            </div>
          </div>
        ))}
        <div className="flex flex-col items-center justify-end flex-shrink-0 h-9 sm:h-10">
          <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-green-100 text-green-400 flex items-center justify-center">
            <Gift className="w-4 h-4" />
          </div>
        </div>
      </div>

      {/* Progress bar — fills left→right, then recedes, in sync with the cups */}
      <div className="w-full h-2 bg-amber-100 rounded-full overflow-hidden mb-3">
        <div ref={fillRef} className="h-full bg-gradient-to-r from-amber-400 to-amber-600 rounded-full" style={{ width: '0%' }} />
      </div>

      <p className="text-center text-sm text-orange-600 max-w-[260px] mx-auto leading-snug">
        Place <strong className="text-orange-700">{N}</strong> coffee orders to unlock a free coffee
      </p>
    </Card>
  );
}

/**
 * Fire a native haptic impact via the runtime Capacitor bridge (mirrors
 * components/BottomNav.js `tapHaptic` — no `@capacitor/haptics` import so the
 * bundle is unchanged). `style` is 'LIGHT' | 'MEDIUM' | 'HEAVY', passed verbatim.
 * Silent no-op on web / PWA and on native builds predating the Haptics plugin.
 */
function tapHaptic(style = 'MEDIUM') {
  try {
    const impact = window?.Capacitor?.Plugins?.Haptics?.impact;
    if (impact) impact({ style });
  } catch {}
}

/**
 * One slot in the punch-card row. Filled slots render the branded takeaway-cup
 * <LottieStampCup/> (bounces + zooms in on app open, staggered by `index`); empty
 * slots render a numbered placeholder. `justEarned` (already AND-ed with
 * !reducedMotion by the caller) layers an ink-ripple behind the cup and fires a
 * haptic as it bounces (HEAVY for the reward-tipping `isTerminal` cup, MEDIUM
 * otherwise). `intro` gates the one-shot so it plays once the card is on screen.
 * Used by: `FreeCoffeeProgress` (full progress row)
 */
function StampSlot({ index, filled, isTerminal, justEarned, intro, reducedMotion }) {
  return (
    <div className="relative flex flex-col items-center justify-end flex-shrink-0 h-9 sm:h-10">
      {filled ? (
        <>
          {justEarned && !reducedMotion && (
            <motion.span
              aria-hidden="true"
              className="absolute left-1/2 bottom-2.5 rounded-full bg-amber-400/40"
              style={{ width: 16, height: 16, x: '-50%' }}
              initial={{ scale: 0, opacity: 0.6 }}
              animate={intro ? { scale: 2.4, opacity: 0 } : { scale: 0, opacity: 0.6 }}
              transition={{ duration: 0.6, delay: index * (STAMP_STAGGER_MS / 1000) + 0.12, ease: 'easeOut' }}
            />
          )}
          <LottieStampCup
            index={index}
            play={intro}
            reducedMotion={reducedMotion}
            onBounce={justEarned ? () => tapHaptic(isTerminal ? 'HEAVY' : 'MEDIUM') : undefined}
            className="w-7 h-9 sm:w-8 sm:h-10"
          />
        </>
      ) : (
        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white border-[1.5px] border-gray-300 text-gray-400 flex items-center justify-center text-xs font-bold">
          {index + 1}
        </div>
      )}
    </div>
  );
}

function ClaimModal({ items, onClaim, onClose, claiming }) {
  return (
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center px-4" data-testid="claim-modal-overlay">
      <div className="bg-card rounded-2xl shadow-2xl p-6 max-w-sm w-full max-h-[80vh] overflow-y-auto animate-in fade-in zoom-in-95 duration-300">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">Choose Your Free Coffee</h2>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-full"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-muted-foreground mb-4">Select one item. Base price is free. Add-ons charged separately.</p>
        <div className="space-y-2">
          {items?.map(item => (
            <button
              key={item.id}
              onClick={() => onClaim(item.id)}
              disabled={claiming}
              className="flex items-center gap-3 w-full p-3 rounded-xl border hover:border-green-300 hover:bg-green-50/50 transition-colors text-left"
              data-testid={`claim-item-${item.id}`}
            >
              <OptimizedImage
                src={item.image_url}
                imageUrls={item.image_urls}
                size="thumb"
                alt={item.name}
                className="w-12 h-12 rounded-lg flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{item.name}</p>
                <p className="text-xs text-muted-foreground">{item.category}</p>
              </div>
              <div className="text-right">
                <p className="text-sm line-through text-muted-foreground">₹{item.price}</p>
                <p className="text-sm font-bold text-green-600">FREE</p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Inline banner showing coffees remaining until free reward; returns null when reward is already unlocked.
 */
export function FreeCoffeeOrderBanner({ remaining, requiredCount, currentCount }) {
  if (!requiredCount || currentCount === undefined) return null;
  const r = Math.max(0, requiredCount - currentCount);
  if (r <= 0) return null;

  return (
    <div className="p-3 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl" data-testid="free-coffee-order-banner">
      <div className="flex items-center gap-2">
        <Coffee className="w-4 h-4 text-amber-700 flex-shrink-0" />
        <p className="text-sm text-amber-900">
          You're <strong>{r}</strong> coffee order{r !== 1 ? 's' : ''} away from a <strong>free coffee</strong>!
        </p>
      </div>
    </div>
  );
}
